import React, { useState } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { Poptart, Sentiment, TypingBubble } from 'sidecar-components'

import { useSidekick } from 'hooks/useSidekick'
import { withAshSession } from 'utils/heap/withAshSession'
import { Sidekick } from './Sidekick'

/**
 * Opening height of the Ash panel, in px. Sized to Ash's tallest routine screen
 * — the "Send a message" compose form — so the member reaches the upload card
 * and the Submit button without scrolling inside the iframe.
 *
 * 600 fit the chat transcript and nothing else. The compose form's boxes add up
 * to roughly 780: the poptart's own chrome (resize handle + header) ~70, Ash's
 * sheet header 48, the fields column ~510 (16px padding top and bottom, the
 * summary banner ~90, the chosen-category row ~21, Subject ~63, and the message
 * group ~254 — a 120px textarea plus the `py-6` upload card — with three 16px
 * gaps), and the pinned Submit footer ~68. A family account adds the "Who is
 * this for?" picker, ~79 more with its gap, which is what this number leaves
 * room for.
 *
 * This is a ceiling, not a floor. `useHeightResize` clamps it to the window
 * (less the safe-area insets), so on a short viewport the panel is
 * viewport-sized and the form scrolls as it did before. The member can still
 * drag the handle either way.
 *
 * Only reached at `xs` (480px) and up. Below that Ash opens maximized, where
 * the panel is the viewport and no height is computed at all — see
 * `isMaximized` below.
 *
 * Mirrored in `apps/member-app/components/sidekick/SidekickPoptart.tsx` — the
 * MFE shell renders its own copy of this poptart. Change both.
 */
const ASH_POPTART_HEIGHT = 780

/**
 * @param {string} className - Optional string to be passed to the poptart.
 *   Mainly used to adjust the z-index for focusing one poptart over another in
 *   mobile.
 * @param {function} onClick - Optional function fired when you click the
 *   poptart. Mainly used to adjust the z-index for focusing one poptart over
 *   another in mobile.
 * @returns {JSX.Element} A Poptart component that contains the Sidekick AI
 *   assistant.
 */
export const SidekickPoptart = ({ className = '', onClick }) => {
  const flags = useFlags()
  const isAshFrontDoor = flags['ash-human-intercept-enabled']
  const {
    isOpen,
    closeSidekick,
    isChatReady,
    isGenesysChatOpen,
    getAshSessionId,
    isMaximized,
    panelHeader,
    postPanelBackPressed,
    setIsMaximized,
  } = useSidekick()
  const [isExpanded, setIsExpanded] = useState(false)

  // On a phone, Ash is the whole screen.
  //
  // Below `xs` the poptart is a `max-xs:fixed bottom-0` sheet whose height is
  // computed, and every term in that sum is somewhere it can come out short: the
  // seed is `window.innerHeight` read when this component mounts at app load
  // (mobile Safari's smallest reading, URL bar expanded) and re-derived only when
  // the viewport itself changes, never when the member opens the panel; it is then
  // clamped against `100dvh` at paint time. That is how Ash reached production
  // opening short near the bottom of the screen, with nothing to suggest that
  // dragging the handle would fix it (BASH-427). Maximized has no height
  // arithmetic at all — `max-xs:!inset-0` and `!max-h-full` — so there is nothing
  // left to compute wrong.
  //
  // `isMaximized` comes from the provider, which reads the viewport once per open
  // and then hands the flag over to the member's maximize button below. It's held
  // out there because this component outlives its own close: it stays mounted, and
  // Radix only reports the closes it handles itself, so a flag kept in here would
  // leak a minimize into the next open.

  // When Genesys chat is open, shift Sidekick left to avoid overlap (desktop only)
  // Only apply offset when not expanded/maximized (full screen modes)
  // The Genesys widget is ~400px wide, so we translate 420px to the left
  // Uses custom CSS class defined in src/index.css
  const isFullScreen = isExpanded || isMaximized
  const genesysOpenClass =
    isGenesysChatOpen && !isFullScreen ? 'sidekick-genesys-offset' : ''

  // This header is the only one the member sees while Ash is showing something that
  // isn't the chat. Ash draws no row of its own when it's embedded, and asks us for
  // the whole thing by `SET_PANEL_HEADER` (`onepoptart.pdf`): the title, and a back
  // arrow when there's a step of its own to return to. `onBack` reports the press;
  // Ash owns what back means, since the steps are its.
  //
  // We pass no `canCollapse`, here or below, so this poptart has never drawn the `—`
  // collapse button. Worth saying because the design's chat frame shows a `—` and its
  // compose frame doesn't: that mark is the resize handle, which both frames have and
  // which is drawn from `isExpanded`, not from anything we or Ash control. There is no
  // collapse control to suppress while the sheet is up.
  //
  // The default title is not gated: "How can we help?" is the header copy for every
  // member, whichever way the flag evaluates. This is the only place that string
  // lives, which is why the provider stores null rather than it.
  //
  // The sparkles icon and Beta pill are still gated on the flag — they brand Ash
  // as an AI side-experience, which is the opposite of the front door being the
  // way members reach support. A retitle leaves them alone: they're already off
  // wherever the compose sheet can be reached, so nothing has to un-brand a header
  // Ash has borrowed.
  //
  // They ride on ash-human-intercept-enabled, the front-door story's single
  // rollout switch, so the branding drops in the same flip that makes the help
  // icon open Ash. Gating them on ash-chat-gate meant a second, independent flag
  // had to be flipped or the front door shipped still wearing a Beta pill.
  //
  // The three tracked handlers below fire out here, not inside the iframe, so the
  // Ash session id has to be read back from the provider. `withAshSession` adds
  // it only once the member has actually started a conversation, and it is read
  // at call time so a session starting while the poptart is open is picked up.
  return (
    <Poptart
      testId="sidekickPoptart"
      isOpen={isOpen}
      // Controlled by the provider, so the viewport can decide it at each open.
      // `Poptart` reads `controlledMaximized ?? internalMaximized`, and its
      // header button still works because it reports through `onMaximizeChange`
      // below.
      isMaximized={isMaximized}
      onClick={onClick}
      onOpenChange={open => {
        if (!open) {
          if (window.heap) {
            window.heap.track('ashClosed', withAshSession(getAshSessionId()))
          }
          // The next open reads the viewport again in `openSidekick`, so
          // minimizing once doesn't stick a phone with the short sheet from
          // then on.
          closeSidekick()
        }
      }}
      onExpandChange={expanded => {
        setIsExpanded(expanded)
        if (window.heap) {
          window.heap.track(
            'ashExpandToggled',
            withAshSession(getAshSessionId(), { expanded }),
          )
        }
      }}
      onMaximizeChange={maximized => {
        setIsMaximized(maximized)
        if (window.heap) {
          window.heap.track(
            'ashMaximizeToggled',
            withAshSession(getAshSessionId(), { maximized }),
          )
        }
      }}
      title={panelHeader?.title ?? 'How can we help?'}
      onBack={panelHeader?.canGoBack ? postPanelBackPressed : undefined}
      titleIcon={isAshFrontDoor ? undefined : 'sparkles'}
      titleIconClass={isAshFrontDoor ? undefined : 'color-iconSidekick-default'}
      // `flex flex-col` so the iframe below can take its height from flex layout
      // instead of a percentage. `Poptart` gives its body a deliberately over-tall
      // `h-[calc(100dvh-theme(spacing.19))]` and lets flex shrink it to the panel —
      // that specified height is what makes the `h-fit` panel resolve tall enough to
      // reach its max-height, so it can't simply go. But a percentage-height child
      // of it resolves against the *specified* height in WebKit rather than the
      // shrunken used height, so Safari sized the iframe ~120px taller than the box
      // holding it and the body's `overflow-y-auto` turned that into a scroll with
      // Ash's composer below the fold. Flex children are measured after the box is,
      // so they get the height that's actually there.
      bodyClass="!p-0 flex flex-col"
      className={`${className} ${genesysOpenClass}`}
      initialHeight={ASH_POPTART_HEIGHT}
      titlePillSentiment={isAshFrontDoor ? undefined : Sentiment.DEFAULT}
      titlePillText={isAshFrontDoor ? undefined : 'Beta'}
    >
      <div className="w-full flex-1 min-h-0 flex flex-col">
        {/* Conditionally render the sparkles, it relies on the response from the iframe */}
        {/* shrink-0 so it takes only the room it needs and leaves the rest to the
            iframe, rather than splitting the column with it */}
        {!isChatReady && (
          <div className="shrink-0">
            <TypingBubble />
          </div>
        )}
        {/* Always render Sidekick so the iframe can load */}
        <Sidekick />
      </div>
    </Poptart>
  )
}
